import { memo, useCallback, type FC } from 'react';
import { Text } from 'Text';
import Button from 'Button';
import { Card } from 'Card';
import { twMerge } from 'tailwind-merge';
import { useTranslation } from 'react-i18next';
import { animatePathChange } from 'RouteAnimation';
import { lessonLaunchPath, streamDashboardPath } from 'Lessons';
import { ClickableText } from 'ClickableText';
import { useNextUpStream, useNextUpLesson } from './hooks';
import { NextUpIcon } from './NextUpIcon';
import { type NextUpBoxPresentationProps } from './NextUpBox.types';

export const NextUpBoxPresentation: FC<NextUpBoxPresentationProps> = ({
    className,
    nextUpStream,
    nextUpLesson,
    hasProgress,
    getOnPress,
}) => {
    const { t } = useTranslation('back_royal', { keyPrefix: 'nextUp.nextUp' });

    return (
        <Card className={twMerge('relative', className)} allowOverflow padding={0}>
            <div className="rounded-16 flex flex-col gap-4 px-2.5 pb-2.5 pt-9.5 md:flex-row md:justify-between md:gap-2 md:px-7 md:pb-8 md:pt-8">
                <div className="flex flex-col items-center md:basis-full md:items-start">
                    <NextUpIcon />
                    <Text as="h2" className="text-center font-sans leading-none md:text-start">
                        {nextUpLesson.title}
                    </Text>
                    <ClickableText
                        onPress={getOnPress('stream')}
                        className="mt-0.5 text-center text-base leading-none hover:text-slate-grey-900 hover:opacity-100 active:text-slate-grey-800 active:opacity-100 md:mt-0 md:text-start"
                        underline="none"
                        color="black"
                    >
                        {nextUpStream.title}
                    </ClickableText>
                </div>
                <div className="flex basis-full items-center justify-center md:flex-shrink md:basis-1/3 md:justify-end">
                    <Button
                        className="w-full uppercase md:w-44"
                        color={hasProgress ? 'purple-gradient' : 'green'}
                        height={9}
                        desktopHeight={8.5}
                        onPress={getOnPress('lesson')}
                    >
                        {t('buttonText', { hasProgress: hasProgress ? 'true' : 'false' })}
                    </Button>
                </div>
            </div>
        </Card>
    );
};

const NextUpBox: FC<{ className?: string }> = memo(({ className }) => {
    const { nextUpStream } = useNextUpStream();
    const { nextUpLesson, hasProgress } = useNextUpLesson(nextUpStream);

    const getOnPress = useCallback(
        (goTo: 'stream' | 'lesson') => () => {
            if (!nextUpStream) return;

            if (goTo === 'stream') {
                animatePathChange(streamDashboardPath({ streamTitle: nextUpStream.title, streamId: nextUpStream.id }));
            } else {
                const chapterIndex = nextUpStream.chapters.findIndex(chapter =>
                    chapter.lessonIds.includes(nextUpLesson.id),
                );
                animatePathChange(
                    lessonLaunchPath({
                        streamId: nextUpStream.id,
                        chapterIndex: chapterIndex >= 0 ? chapterIndex : 0,
                        lessonId: nextUpLesson.id,
                    }),
                );
            }
        },
        [nextUpStream, nextUpLesson],
    );

    if (!nextUpStream) return null;

    return (
        <NextUpBoxPresentation
            className={className}
            nextUpStream={nextUpStream}
            nextUpLesson={nextUpLesson}
            hasProgress={hasProgress}
            getOnPress={getOnPress}
        />
    );
});

export const NextUpMobileOnly = memo(() => <NextUpBox className="block md:hidden" />);
export const NextUpDesktopOnly = memo(() => <NextUpBox className="hidden md:block" />);
